import { Button } from "@/components/Button";
import { Carousel } from "@mantine/carousel";
import { useEffect, useState, useMemo, useCallback, memo, useRef } from "react";
import { useMediaQuery, useDisclosure } from "@mantine/hooks";
import { api, type SidebarCategory, type Product } from "@/lib/api";
import { useNavigate } from "react-router";
import { useApplicationForm } from "@/lib/ApplicationFormContext";
import { motion } from "framer-motion";
import { Modal } from "@mantine/core";
import { IconX, IconZoomIn, IconZoomOut, IconArrowsMaximize } from "@tabler/icons-react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

interface CategoryWithProducts extends SidebarCategory {
  products?: Product[];
}

// Custom scrollable tabs component
const ScrollableTabs = memo(
  ({
    items,
    activeIndex,
    onTabClick,
    isLg,
  }: {
    items: Array<{ id: number; name: string }>;
    activeIndex: number | undefined;
    onTabClick: (index: number) => void;
    isLg: boolean;
  }) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

    const centerTab = useCallback((index: number) => {
      const container = scrollContainerRef.current;
      const tab = tabRefs.current[index];

      if (!container || !tab) return;

      const containerWidth = container.offsetWidth;
      const tabLeft = tab.offsetLeft;
      const tabWidth = tab.offsetWidth;

      // Calculate scroll position to center the tab
      const scrollPosition = tabLeft - containerWidth / 2 + tabWidth / 2;

      container.scrollTo({
        left: scrollPosition,
        behavior: "smooth",
      });
    }, []);

    useEffect(() => {
      if (typeof activeIndex !== "undefined") {
        centerTab(activeIndex);
      }
    }, [activeIndex, centerTab]);

    return (
      <div
        ref={scrollContainerRef}
        className="overflow-x-auto overflow-y-hidden scrollbar-hide"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        <div
          className="flex gap-2.5 min-w-min"
          style={{ paddingLeft: isLg ? "60px" : "16px", paddingRight: "16px" }}
        >
          {items.map((item, idx) => (
            <Button
              key={item.id}
              //@ts-ignore
              ref={(el) => {
                tabRefs.current[idx] = el;
              }}
              className="transition-all whitespace-nowrap"
              variant={idx === activeIndex ? "filled" : "outline"}
              onClick={() => onTabClick(idx)}
            >
              {item.name}
            </Button>
          ))}
        </div>
      </div>
    );
  }
);

ScrollableTabs.displayName = "ScrollableTabs";

// Memoized product card component
const ProductCard = memo(
  ({
    product,
    isLg,
    onProductClick,
  }: {
    product: Product;
    isLg: boolean;
    onProductClick: (product: Product) => void;
  }) => {
    const productImage = useMemo(() => {
      return product.image.startsWith("http")
        ? product.image
        : `${process.env.API_BASE_URL}/${product.image}`;
    }, [product.image]);

    return (
      <Carousel.Slide key={product.id}>
        <div
          className="h-112 w-full relative overflow-hidden group rounded-[30px] cursor-pointer"
          onClick={() => onProductClick(product)}
        >
          {/* Use img tag instead of background-image for better performance */}
          <img
            src={productImage}
            alt={product.name}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform group-hover:scale-110 duration-500"
          />
          {/* Simplified blur effect for mobile, full effect for desktop */}
          {isLg && (
            <div
              className="absolute scale-110 inset-0 transition-opacity blur-xl opacity-80"
              style={{
                maskImage: `linear-gradient(to top, white 0%, white 35%, transparent 50%)`,
                WebkitMaskImage: `linear-gradient(to top, black 0%, black 25%, transparent 50%)`,
                backgroundImage: `url(${productImage})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
          )}
          <div
            className={`absolute bottom-0 transition-all group-hover:bottom-0 p-3.75 text-white flex flex-col gap-2 bg-linear-to-t from-black/70 to-transparent pointer-events-none`}
          >
            <span className="text-[28px] font-medium leading-7">{product.name}</span>
            <span className="text-sm leading-[120%]">
              {product.description}
            </span>
          </div>
        </div>
      </Carousel.Slide>
    );
  }
);

ProductCard.displayName = "ProductCard";

export function MainProjects() {
  const { openForm } = useApplicationForm();
  const [active, setActive] = useState<number>();
  const [activeSub, setActiveSub] = useState<number>();
  const isLg = useMediaQuery("(min-width: 1024px)");
  const [categories, setCategories] = useState<CategoryWithProducts[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const navigate = useNavigate();
  const [modalOpened, { open: openModal, close: closeModal }] =
    useDisclosure(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const transformRef = useRef<any>(null);
  const [zoom, setZoom] = useState(1);
  const isMobile = useMediaQuery("(max-width: 768px)");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch categories with subcategories
      const categoriesData = await api.getSidebar();

      setCategories(categoriesData);

      if (categoriesData.length > 0) {
        setActive(0);
        setActiveSub(0);
      }
    } catch (err) {
      console.error("Failed to load data:", err);
    }
  };

  const handleChangeCategory = useCallback(
    (idx: number) => {
      if (idx < 0 || idx >= categories.length) return;
      setActive(idx);
      const category = categories[idx];

      if (category && category.subCategories.length > 0) setActiveSub(0);
    },
    [categories]
  );

  // Memoize filtered products to avoid recalculation on every render
  const currentSubCategoryId = useMemo(() => {
    if (typeof active === "undefined" || typeof activeSub === "undefined")
      return null;
    return categories[active]?.subCategories?.[activeSub]?.id;
  }, [active, activeSub, categories]);

  useEffect(() => {
    if (!currentSubCategoryId) {
      setProducts([]);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setProductsLoading(true);
        const response = await api.getProducts(1, 12, [currentSubCategoryId]);
        if (!cancelled) setProducts(response.data);
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load products:", err);
          setProducts([]);
        }
      } finally {
        if (!cancelled) setProductsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentSubCategoryId]);

  const handleOpenForm = useCallback(
    (productId: number) => {
      openForm({ productId });
      closeModal();
    },
    [openForm, closeModal]
  );

  const handleProductClick = useCallback(
    (product: Product) => {
      setSelectedProduct(product);
      setZoom(1);
      openModal();
    },
    [openModal]
  );

  const handleZoomIn = useCallback(() => {
    if (transformRef.current) {
      transformRef.current.zoomIn();
    }
  }, []);

  const handleZoomOut = useCallback(() => {
    if (transformRef.current) {
      transformRef.current.zoomOut();
    }
  }, []);

  const handleResetZoom = useCallback(() => {
    if (transformRef.current) {
      transformRef.current.resetTransform();
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && modalOpened) {
        closeModal();
      }
    };

    if (modalOpened) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [modalOpened, closeModal]);

  const handleNavigateToCatalog = useCallback(() => {
    navigate("/catalog");
  }, [navigate]);

  const handleSubCategoryChange = useCallback((idx: number) => {
    setActiveSub(idx);
  }, []);

  const currentSubCategories = useMemo(() => {
    if (typeof active === "undefined") return [];
    return categories[active]?.subCategories || [];
  }, [active, categories]);

  return (
    <div className="max-w-360 mx-auto py-10" id="projects">
      <motion.h2
        className="text-[40px] leading-[120%] px-4 font-medium text-main text-center lg:hidden"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        Несколько наших работ
      </motion.h2>

      {/* Categories Tabs */}
      <motion.div
        className="mt-4 lg:mt-0"
        initial={{ opacity: 0, x: -30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <ScrollableTabs
          items={categories}
          activeIndex={active}
          onTabClick={handleChangeCategory}
          isLg={isLg}
        />
      </motion.div>

      <motion.div
        className="lg:hidden px-4 text-lg opacity-80 text-main font-medium text-center mt-4"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 0.8 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.5 }}
      >
        Фрагменты реализованных интерьеров и мебельных решений для кафе, офисов и жилых пространств.
      </motion.div>

      {/* Subcategories tabs */}
      {currentSubCategories.length > 0 && (
        <div className="mt-7.5">
          <ScrollableTabs
            items={currentSubCategories}
            activeIndex={activeSub}
            onTabClick={handleSubCategoryChange}
            isLg={isLg}
          />
        </div>
      )}
      <div className="mt-7.5 h-auto flex gap-14 lg:h-112 px-4 lg:px-15 w-full">
        <motion.div
          className="min-w-76 max-w-76 h-full hidden lg:flex flex-col justify-between"
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <div>
            <h2 className="text-[40px] text-main font-medium leading-[120%]">
              Несколько наших работ
            </h2>
            <p className="text-lg font-medium text-main leading-[120%] mt-6">
              Фрагменты реализованных интерьеров и мебельных решений для кафе, офисов и жилых пространств.
            </p>
          </div>
          <div>
            <Button onClick={handleNavigateToCatalog}>
              Перейти в полный раздел
            </Button>
          </div>
        </motion.div>
        <motion.div
          className="h-full w-full lg:overflow-hidden"
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <Carousel
            styles={{
              indicators: {
                bottom: -20,
              },
            }}
            slideSize={343}
            slideGap={24}
            withControls={isLg && products.length > 3}
            withIndicators={!isLg}
            emblaOptions={{
              dragFree: false,
              loop: false,
            }}
          >
            {products.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                isLg={isLg}
                onProductClick={handleProductClick}
              />
            ))}
          </Carousel>
        </motion.div>
      </div>

      <div className="text-center mt-11 flex flex-col px-4 lg:hidden items-center gap-4">
        <p className="text-lg opacity-80 font-medium ">Свайпните в сторону</p>
        <Button onClick={handleNavigateToCatalog}>
          Перейти в полный раздел
        </Button>
      </div>

      {/* Fullscreen Image Modal with Zoom */}
      <Modal
        opened={modalOpened}
        onClose={closeModal}
        fullScreen
        padding={0}
        withCloseButton={false}
        classNames={{
          content: "!bg-black/95 !shadow-none",
          body: "!p-0 !h-full",
          inner: "!z-[9999]",
          overlay: "!z-[9998]",
        }}
      >
          {selectedProduct && (
            <div className="relative w-full h-full bg-gradient-to-br from-black via-gray-900 to-black">
              {/* Controls */}
              <div className="absolute top-4 right-4 z-50 flex gap-2 items-center flex-wrap justify-end">
                <div className="flex gap-2 items-center bg-black/40 backdrop-blur-lg rounded-full px-2 py-1.5 border border-white/10 shadow-xl">
                  <button
                    onClick={handleZoomOut}
                    className="w-9 h-9 flex items-center justify-center bg-white/5 hover:bg-white/15 text-white rounded-full transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:scale-110 active:scale-95"
                    aria-label="Уменьшить"
                    disabled={zoom <= 0.5}
                  >
                    <IconZoomOut size={18} stroke={2.5} />
                  </button>
                  <span className="px-3 py-1 text-white text-xs font-semibold min-w-[50px] text-center">
                    {Math.round(zoom * 100)}%
                  </span>
                  <button
                    onClick={handleZoomIn}
                    className="w-9 h-9 flex items-center justify-center bg-white/5 hover:bg-white/15 text-white rounded-full transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:scale-110 active:scale-95"
                    aria-label="Увеличить"
                    disabled={zoom >= 5}
                  >
                    <IconZoomIn size={18} stroke={2.5} />
                  </button>
                  {zoom !== 1 && (
                    <button
                      onClick={handleResetZoom}
                      className="w-9 h-9 flex items-center justify-center bg-white/5 hover:bg-white/15 text-white rounded-full transition-all hover:scale-110 active:scale-95 ml-1"
                      aria-label="Сбросить масштаб"
                    >
                      <IconArrowsMaximize size={18} stroke={2.5} />
                    </button>
                  )}
                </div>
                <button
                  onClick={closeModal}
                  className="w-9 h-9 flex items-center justify-center bg-red-500/20 hover:bg-red-500/40 backdrop-blur-lg text-white rounded-full transition-all border border-red-500/30 hover:border-red-500/50 shadow-xl hover:scale-110 active:scale-95"
                  aria-label="Закрыть"
                >
                  <IconX size={18} stroke={2.5} />
                </button>
              </div>

            {/* Image Container with Zoom/Pan */}
            <TransformWrapper
              ref={transformRef}
              initialScale={1}
              minScale={0.5}
              maxScale={5}
              wheel={{ step: 0.1 }}
              panning={{ disabled: !isMobile }}
              doubleClick={{ disabled: true }}
              centerOnInit
              limitToBounds={false}
              centerZoomedOut={true}
              onZoom={(ref) => {
                const scale = (ref as any).state?.scale || 1;
                setZoom(scale);
              }}
              onTransformed={(ref) => {
                const scale = (ref as any).state?.scale || 1;
                setZoom(scale);
              }}
            >
              {({ zoomIn, zoomOut, resetTransform }) => {
                // Store functions for external controls
                if (transformRef.current) {
                  (transformRef.current as any).zoomIn = zoomIn;
                  (transformRef.current as any).zoomOut = zoomOut;
                  (transformRef.current as any).resetTransform = resetTransform;
                }

                return (
                  <TransformComponent
                    wrapperStyle={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    contentStyle={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    <img
                      src={
                        selectedProduct.image.startsWith("http")
                          ? selectedProduct.image
                          : `${process.env.API_BASE_URL}/${selectedProduct.image}`
                      }
                      alt={selectedProduct.name}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        userSelect: "none",
                        WebkitUserSelect: "none",
                      }}
                      draggable={false}
                    />
                  </TransformComponent>
                );
              }}
            </TransformWrapper>

            {/* Product Info Overlay - Only visible when not zoomed */}
            {zoom === 1 && (
              <div className="absolute bottom-0 left-0 right-0 p-4 lg:p-6 bg-gradient-to-t from-black via-black/95 to-transparent pointer-events-none z-40">
                <div className="pointer-events-auto max-w-2xl mx-auto">
                  <h3 className="text-white text-2xl lg:text-4xl font-semibold mb-3 drop-shadow-lg">
                    {selectedProduct.name}
                  </h3>
                  <p className="text-white/90 text-sm lg:text-lg mb-4 lg:mb-6 leading-relaxed drop-shadow-md">
                    {selectedProduct.description}
                  </p>
                  <Button
                    variant="white"
                    onClick={() => handleOpenForm(selectedProduct.id)}
                    className="shadow-xl hover:shadow-2xl transition-all hover:scale-105"
                  >
                    <span className="text-main font-semibold">Отправить заявку</span>
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
